import { formatAppDate, dateLocale } from '../utility/dateLocale';
import { invoiceMonthValue } from './InvoiceMonthFilter';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { resolveInvoiceStatus } from '../utility/invoiceStatus';
import { useEffect, useState, type ReactNode } from 'react';
import { Group, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { INVOICE_DATE_COLUMNS, invoiceDateColumn, invoicePaymentDate } from '../utility/invoiceKanbanDates';
import { IconFileInvoice, IconCalendar, IconBuilding } from '@tabler/icons-react';
import './InvoiceKanbanView.css';

type InvoiceCard = {
  id: string;
  name: string;
  issue_date: string;
  total_amount: number;
  paid_amount: number;
  client_name: string;
  owner_name?: string;
  invoice_status?: string;
  has_factura_attachment?: boolean;
};
type Props<T extends InvoiceCard> = {
  invoices: T[];
  toolbarFilters?: ReactNode;
  selectedMonth?: string;
  showAmounts: boolean;
  onOpen: (invoice: T) => void;
  invoiceNumbers: Record<string, { num: number; total: number }>;
};
export function InvoiceKanbanView<T extends InvoiceCard>({ invoices, onOpen, invoiceNumbers, showAmounts, toolbarFilters, selectedMonth = invoiceMonthValue(new Date()) }: Props<T>) {
  const { t, i18n } = useTranslation();
  const [today, setToday] = useState(() => new Date());
  const [year, month] = selectedMonth.split('-').map(Number);
  const monthDate = selectedMonth === 'all' ? undefined : new Date(year, month - 1, 1);
  const formattedMonth = (monthDate ?? today).toLocaleDateString(dateLocale(i18n.language), { month: 'long', year: 'numeric' });
  const monthLabel = formattedMonth.charAt(0).toLocaleUpperCase(i18n.language) + formattedMonth.slice(1);
  const shortDate = (date: Date) => date.toLocaleDateString(dateLocale(i18n.language), { day: '2-digit', month: '2-digit' });
  const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (today.getDay() + 6) % 7);
  const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
  const columnTitle = (key: string) => {
    if (key === 'thisMonth') return selectedMonth === 'all' ? t('invoices.kanban.allMonths') : monthLabel;
    if (key === 'delayed' || key === 'completed') return t(`invoices.kanban.${key}`);
    const dates = key === 'today' ? shortDate(today) : `${shortDate(weekStart)} – ${shortDate(weekEnd)}`;
    return `${t(`invoices.kanban.${key}`)} · ${dates}`;
  };
  useEffect(() => {
    const refresh = () => setToday(new Date());
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, []);
  const money = (value: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(value);
  const grouped = INVOICE_DATE_COLUMNS.map(key => ({
    key,
    invoices: invoices.filter(invoice => invoiceDateColumn(invoice.issue_date, today, monthDate, invoice.has_factura_attachment, invoice.invoice_status, selectedMonth === 'all') === key)
      .sort((a, b) => a.issue_date.localeCompare(b.issue_date) || a.id.localeCompare(b.id)),
  }));
  return <div className="invoice-kanban-view">
    {toolbarFilters && <div className="invoice-kanban-toolbar">{toolbarFilters}</div>}
    <div className="invoice-kanban" aria-label={t('invoices.kanban.title')}>
      {grouped.map(column => <section key={column.key} className={`invoice-kanban-column invoice-kanban-${column.key}`} aria-label={columnTitle(column.key)}>
        <header className="invoice-kanban-header">
          <Group position="apart" noWrap spacing="xs">
            <Text component="h2" size="sm" weight={700} m={0}>{columnTitle(column.key)}</Text>
            <div className="invoice-column-kpi" aria-label={t('invoices.kanban.ticketCount', { count: column.invoices.length })}>
              <span className="invoice-column-kpi-number">{new Intl.NumberFormat(i18n.language).format(column.invoices.length)}</span>
              <span className="invoice-column-kpi-label">{t('invoices.kanban.tickets')}</span>
            </div>
          </Group>
          {showAmounts && <Text size="sm" mt={6}>{money(column.invoices.reduce((sum, invoice) => sum + invoice.total_amount, 0))}</Text>}
        </header>
        <div className="invoice-kanban-cards">
          {!column.invoices.length && <Text size="sm" align="center" color="dimmed" py="lg">{t('invoices.kanban.empty')}</Text>}
          {column.invoices.map(invoice => {
            const status = resolveInvoiceStatus(invoice.issue_date, Boolean(invoice.has_factura_attachment), invoice.invoice_status, today);
            const number = invoiceNumbers[invoice.id];
            const date = invoicePaymentDate(invoice.issue_date);
            return <UnstyledButton key={invoice.id} className="invoice-kanban-card" onClick={() => onOpen(invoice)}>
              <div className="invoice-card-top">
                <div className="invoice-card-reference">
                  <IconFileInvoice size={16} className="invoice-card-icon" aria-hidden="true" />
                  <Text size="xs" color="dimmed">{number ? t('invoices.invoiceNumberOf', { num: number.num, total: number.total }) : `#${invoice.id.slice(0, 8)}`}</Text>
                </div>
                <InvoiceStatusBadge status={status} paymentDate={invoice.issue_date} />
              </div>
              <Text className="invoice-card-name" weight={700} title={invoice.name}>{invoice.name || t('invoices.title')}</Text>
              <div className="invoice-card-owner" title={t('opportunity.filters.owner')}>
                <span className="invoice-card-initials" aria-hidden="true">{invoice.owner_name?.trim().split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase() || '—'}</span>
                <Text size="xs" weight={500}>{invoice.owner_name || '—'}</Text>
              </div>
              <div className="invoice-card-client">
                <IconBuilding size={15} aria-hidden="true" />
                <div>
                  <Text className="invoice-card-label">{t('invoices.client')}</Text>
                  <Text className="invoice-card-client-name">{invoice.client_name || '—'}</Text>
                </div>
              </div>
              <div className="invoice-card-date">
                <div className="invoice-card-date-label">
                  <IconCalendar size={15} aria-hidden="true" />
                  <Text className="invoice-card-label">{t('invoices.kanban.paymentDate')}</Text>
                </div>
                <Text component="time" dateTime={invoice.issue_date.slice(0,10)} className="invoice-card-date-value">{formatAppDate(date,i18n.language)}</Text>
              </div>
              {showAmounts && <div className="invoice-card-amount">
                <Text size="sm" weight={700}>{money(invoice.total_amount)}</Text>
                {invoice.paid_amount > 0 && <Text size="xs" color="teal">{t('invoices.kanban.paidAmount', { amount: money(invoice.paid_amount) })}</Text>}
              </div>}
            </UnstyledButton>;
          })}
        </div>
      </section>)}
    </div>
  </div>;
}
